import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Checkbox } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SignModal from "./SignModal";
import EditSignModal from "./EditSignModal";
import DeleteConfirmation from "./DeleteConfirmation";

import { API_BASE_URL } from "../../../apiConfig";
import AddSignModal from "./AddSignModal";
import { refreshTokenIfNeeded } from "../refreshTokenIfNeeded";

export default function ManageSign() {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [nameToDelete, setNameToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async (page) => {
    const token = localStorage.getItem("token");

    if (!token) {
      return navigate("/", { replace: true });
    }

    const handleResponse = (response) => {
      if (response.data.ads && response.data.ads.length > 0) {
        const formattedData = response.data.ads.map((ads) => ({
          id: ads.id,
          imageURL: ads.imageURL,
          slug: ads.slug,
        }));

        setRows(formattedData);
        setTotalRecords(response.data.allRecord);
      } else {
        setRows([]);
        setTotalRecords(0);
      }
    };

    try {
      const response = await axios.get(
        `${API_BASE_URL}Ads/GetAllByPage/${page + 1}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      handleResponse(response);
    } catch (error) {
      if (error.response?.status === 401) {
        try {
          const newToken = await refreshTokenIfNeeded();

          if (!newToken) {
            return navigate("/", { replace: true });
          }

          // إعادة الطلب بالتوكن الجديد
          const retryResponse = await axios.get(
            `${API_BASE_URL}Ads/GetAllByPage/${page + 1}`,
            {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            },
          );

          handleResponse(retryResponse);
        } catch (retryError) {}
      } else {
        setRows([]);
        setTotalRecords(0);
      }
    }
  };

  const handlePaginationChange = (paginationModel) => {
    setCurrentPage(paginationModel.page);
  };

  const handleSearch = () => {
    //setCurrentPage(0);
    setTriggerSearch(!triggerSearch);
  };

  const handleClear = () => {
    setSearchValue("");
    //setCurrentPage(0);
    setTriggerSearch(!triggerSearch);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // useEffect(() => {
  //   fetchData(currentPage, searchValue);
  // }, [currentPage, triggerSearch]);

  // useEffect(() => {
  //   fetchData(currentPage, searchValue);
  // });

  useEffect(() => {
    fetchData(currentPage || 0, searchValue || "");
  }, [currentPage, triggerSearch]);

  const handleDeleteClick = (id, name) => {
    setUserIdToDelete(id);
    setNameToDelete(name);
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    const token = localStorage.getItem("token");

    if (userIdToDelete === 1) {
      return;
    }

    axios
      .delete(`${API_BASE_URL}Ads/Delete/${userIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        alert("تم حذف الإعلان بنجاح.");
        setShowConfirm(false);
        setUserIdToDelete(null);
        fetchData(currentPage, searchValue);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          refreshTokenIfNeeded()
            .then((newToken) => {
              if (!newToken) {
                navigate("/", { replace: true });
                return;
              }

              return axios.delete(
                `${API_BASE_URL}Ads/Delete/${userIdToDelete}`,
                {
                  headers: {
                    Authorization: `Bearer ${newToken}`,
                  },
                },
              );
            })
            .then(() => {
              alert("تم حذف الإعلان بنجاح.");
              setShowConfirm(false);
              setUserIdToDelete(null);
              fetchData(currentPage, searchValue);
            })
            .catch((retryError) => {
              alert("Retry failed: " + retryError.message);
            });
        } else {
          alert("حدث خطأ أثناء محاولة حذف الإعلان.");
        }
      });
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
    setUserIdToDelete(null);
  };

  const handleEdit = (id) => {
    setSelectedUserId(id);
    setEditUserModalOpen(true);
  };

  const handleView = (id) => {
    setSelectedUserId(id);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUserId(null);
  };

  const handleAddRow = () => {
    setAddUserModalOpen(true);
  };

  const handleCloseAddUserModal = () => {
    fetchData(currentPage, searchValue);
    setAddUserModalOpen(false);
  };

  const handleCloseEditUserModal = () => {
    fetchData(currentPage, searchValue);
    setEditUserModalOpen(false);
  };

  const columns = [
    {
      field: "actions",
      headerName: "الإجراءات",
      flex: 1.5,
      renderCell: (params) => (
        <>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row.id, params.row.slug)}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={() => handleEdit(params.row.id)}>
            <EditIcon sx={{ color: "green" }} />
          </IconButton>
          <IconButton color="primary" onClick={() => handleView(params.row.id)}>
            <VisibilityIcon />
          </IconButton>
        </>
      ),
      headerAlign: "right",
      align: "right",
    },

    {
      field: "slug",
      headerName: "اسم الرابط",
      flex: 9,
      headerAlign: "right",
      align: "right",
    },
    {
      field: "imageURL",
      headerName: "صورة الإعلان",
      flex: 1,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: 100, // عرض المربع
            height: 50, // ارتفاع المربع
            overflow: "hidden",
            borderRadius: "5px",
            //  border: "1px solid #ccc", // إضافة حدود
          }}
        >
          <img
            src={params.value}
            alt="ads"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      ),
    },
  ];

  return (
    <div
      dir={"rtl"}
      style={{
        color: "#272727",
        maxWidth: "100%",
        maxHeight: "100%",
        fontFamily: "'Changa', sans-serif",
        textAlign: "center",
        paddingTop: "10px",
        fontWeight: "bold",
      }}
    >
      إدارة الإعلانات في رئيسية الموقع
      <div
        dir={"ltr"}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "2%",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "1%",
          }}
        >
          <Button variant="contained" color="primary" onClick={handleAddRow}>
            اضف إعلان
          </Button>
        </div>

        <Box
          sx={{
            height: "67%",
            width: "100%",
          }}
        >
          <DataGrid
            style={{ background: "#f3f3f3" }}
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 3,
                },
              },
            }}
            pageSizeOptions={[3]}
            paginationMode="server"
            rowCount={totalRecords}
            disableRowSelectionOnClick
            // onPaginationModelChange={handlePaginationChange}
            onPaginationModelChange={(model) => {
              setIsLoading(true); // عند بدء تحميل الصفحة، تفعيل حالة التحميل
              handlePaginationChange(model);
              setIsLoading(false); // إلغاء حالة التحميل بعد استرجاع البيانات
            }}
            disableColumnFilter={true}
            disableColumnSelector={true}
            disableColumnMenu={true}
            disableColumnSorting={true}
            autoHeight={true}
            localeText={{
              noRowsLabel: "",
            }}
            sx={{
              "& .MuiTablePagination-actions > button": {
                transform: "rotate(180deg)",
              },
              "& .MuiDataGrid-columnHeaders": {
                direction: "ltr",
                textAlign: "right",
              },
              "& .MuiDataGrid-cell": {
                direction: "ltr",
                textAlign: "right",
              },
              "& .MuiDataGrid-footerContainer": {
                justifyContent: "flex-start",
              },
              "& .MuiTablePagination-toolbar": {
                direction: "rtl",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none",
              },
            }}
          />
        </Box>
        {modalOpen && (
          <SignModal userId={selectedUserId} onClose={handleCloseModal} />
        )}
        {addUserModalOpen && (
          <AddSignModal
            onClose={handleCloseAddUserModal}
            onSubmit={fetchData}
          />
        )}
        {editUserModalOpen && (
          <EditSignModal
            storeId={selectedUserId}
            onClose={handleCloseEditUserModal}
            onSubmit={fetchData}
          />
        )}
        {showConfirm && (
          <DeleteConfirmation
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            nameUser={nameToDelete}
          />
        )}
      </div>
    </div>
  );
}

// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import Box from "@mui/material/Box";
// import Button from "@mui/material/Button";
// import { DataGrid } from "@mui/x-data-grid";
// import { IconButton } from "@mui/material";
// import DeleteIcon from "@mui/icons-material/Delete";
// import EditIcon from "@mui/icons-material/Edit";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import UserModal from "./UserModal";

// export default function ManageUsers() {
//   const [rows, setRows] = useState([]);
//   const [totalRecords, setTotalRecords] = useState(0);
//   const navigate = useNavigate();
//   const [currentPage, setCurrentPage] = useState(0);
//   const [selectedUserId, setSelectedUserId] = useState(null);
//   const [modalOpen, setModalOpen] = useState(false);

//   const handlePaginationChange = (paginationModel) => {
//     setCurrentPage(paginationModel.page);
//   };

//   //   useEffect(() => {
//   //     const fetchData = async () => {
//   //       const token = localStorage.getItem("token");

//   //       if (!token) {
//   //         console.error("Token is missing, redirecting to login.");
//   //         navigate("/login");
//   //         return;
//   //       }

//   //       try {
//   //         const response = await axios.get(
//   //           `https://webapplication120240901063509.azurewebsites.net/api/User/GetAllByPage/${
//   //             currentPage + 1
//   //           }`,
//   //           {
//   //             headers: {
//   //               Authorization: `Bearer ${token}`,
//   //             },
//   //           }
//   //         );

//   //         const formattedData = response.data.users.map((user) => ({
//   //           id: user.id,
//   //           fullName: user.fullName,
//   //           userName: user.userName,
//   //         }));

//   //         setRows(formattedData);
//   //         setTotalRecords(response.data.allRecord);
//   //       } catch (error) {
//   //         console.error("Error fetching data:", error);
//   //       }
//   //     };

//   //     fetchData();
//   //   }, [currentPage, navigate]);

//   const handleDelete = (id) => {
//     const storedUserId = Number(localStorage.getItem("userID"));

//     if (id === storedUserId) {
//       alert(`You are connected to more than one table ${id}`);
//     } else {
//       alert(`You do not have permission to Delete User ${id}`);
//     }
//   };

//   const handleEdit = (id) => {
//     const storedUserId = Number(localStorage.getItem("userID"));

//     if (id === storedUserId) {
//       navigate("/infoUser");
//     } else {
//       alert(`You do not have permission to Edit User ${id}`);
//     }
//   };

//   const handleView = (id) => {
//     setSelectedUserId(id);
//     setModalOpen(true);
//   };

//   const handleCloseModal = () => {
//     setModalOpen(false);
//     setSelectedUserId(null);
//   };

//   const handleAddRow = () => {
//     alert(`You do not have permission to Add New User`);
//   };

//   const columns = [
//     {
//       field: "actions",
//       headerName: "Actions",
//       flex: 1,
//       renderCell: (params) => (
//         <>
//           <IconButton color="primary" onClick={() => handleView(params.row.id)}>
//             <VisibilityIcon />
//           </IconButton>
//           <IconButton
//             color="secondary"
//             onClick={() => handleEdit(params.row.id)}
//           >
//             <EditIcon />
//           </IconButton>
//           <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
//             <DeleteIcon />
//           </IconButton>
//         </>
//       ),
//     },

//     {
//       field: "userName",
//       headerName: "User Name",
//       flex: 1.5,
//     },

//     {
//       field: "fullName",
//       headerName: "Full Name",
//       flex: 1.5,
//     },
//     { field: "id", headerName: "ID", flex: 0.5 },
//   ];

//   return (
//     <div
//       dir={"ltr"}
//       style={{
//         display: "flex",
//         flexDirection: "column",
//         alignItems: "center",
//         marginTop: "7%",
//       }}
//     >
//       <div
//         style={{
//           width: "100%",
//           display: "flex",
//           justifyContent: "flex-start",
//           marginBottom: "1%",
//         }}
//       >
//         <Button variant="contained" color="primary" onClick={handleAddRow}>
//           Add User
//         </Button>
//       </div>

//       <Box
//         sx={{
//           height: "67%",
//           width: "100%",
//         }}
//       >
//         <DataGrid
//           style={{ background: "#e6e6e6" }}
//           rows={rows}
//           columns={columns}
//           getRowId={(row) => row.id}
//           initialState={{
//             pagination: {
//               paginationModel: {
//                 pageSize: 10,
//               },
//             },
//           }}
//           pageSizeOptions={[10]}
//           paginationMode="server"
//           rowCount={totalRecords}
//           disableRowSelectionOnClick
//           onPaginationModelChange={handlePaginationChange}
//         />
//       </Box>
//       {modalOpen && (
//         <UserModal userId={selectedUserId} onClose={handleCloseModal} />
//       )}
//     </div>
//   );
// }
